<!-- 
Panel containing invoice details for a levy collection provider.
-->
<template>
  <div>
    <our-loading-indicator v-if="listing.loading"/>
    <div v-else>
      <our-empty-tab-message v-if="noInvoices && !hasCalculation">
        The levy has not yet been calculated for this provider.
      </our-empty-tab-message>
      <div v-if="canPreview">
        <div v-if="noInvoices && hasCalculation">
          <our-empty-tab-message bottom-margin="3">
            This provider does not have any invoices yet.
            <br>
            You can view a preview of the invoice PDF using the buttons below (dummy values will be used for some SAP fields).
          </our-empty-tab-message>
        </div>
        <div v-if="allInvoicesInvalid">
          <our-empty-tab-message bottom-margin="3">
            All existing invoices have either been reversed, written off or rejected.
            <br>
            You can view a preview of the invoice PDF based on the current levy calculation by using the buttons below
            (dummy values will be used for some SAP fields).
          </our-empty-tab-message>
        </div>
        <v-btn v-if="smAndUp" flat color="primary" @click="loadPreviewPdf">
          Preview PDF
          <our-expand-icon :expanded="showingPreview"/>
        </v-btn>
        <v-btn :to="`/levy-collections/${levyCollectionId}/providers/${providerId}/pdf-preview`" color="primary" flat target="_blank">
          Preview PDF in new window
          <v-icon class="ml-2">open_in_new</v-icon>
        </v-btn>
        <div v-if="showingPreview && smAndUp" style="min-height: 100px">
          <v-progress-circular
            indeterminate
            color="primary"
            style="position:absolute;z-index:1;left:50px"
          />
          <embed
            v-if="previewPdfUrl"
            :src="previewPdfUrl"
            type="application/pdf" width="100%" height="500"
            style="position:relative;z-index:2;"
          >
        </div>
      </div>
      <div v-if="!noInvoices">
        <v-divider v-if="allInvoicesInvalid" />
        <invoice-listing
          :levy-collection-id="levyCollectionId"
          :provider-id="providerId"
          default-no-data-text="This provider does not have any invoices yet."
          embedded
          @click="open"
        />
      </div>
    </div>
  </div>
</template>

<script>
/* 
Levy collection provider invoice panel
--------------------------------------

Panel showing invoice details for a LCP. If there are real invoices for the LCP then the invoice listing
will be shown; otherwise it will show a preview of the invoice with dummy values substituted for some of the 
SAP info.
*/
import InvoiceTransactions from "@/components/invoices/InvoiceTransactions.vue";
import InvoiceListing from "@/components/invoices/InvoiceListing.vue";
import tableMixins from "@/mixins/table.mixins";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import * as action from "@/store/invoices/action-types";
import * as mutation from "@/store/invoices/mutation-types";

export default {
  components: { InvoiceTransactions, InvoiceListing },
  mixins: [tableMixins, filterMixins, lookupMixins],
  props: {
    levyCollectionId: {
      type: Number,
      required: true
    },
    providerId: {
      type: Number,
      required: true
    },
    hasCalculation: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      action: action.LIST,
      showingPreview: false,
      previewPdfUrl: null,
      pdfUrls: {},
      showing: [],
      loaded: []
    };
  },
  computed: {
    listing() {
      return this.$store.state.invoices.listing;
    },
    listingLoaded() {
      return this.listing.loaded;
    },
    noInvoices() {
      return this.listing.loaded && this.listing.paging.totalItems === 0;
    },
    allInvoicesInvalid() {
      return !this.noInvoices && this.listing.data.every(i => !this.isValidStatus(i.status));
    },
    canPreview() {
      return (this.allInvoicesInvalid && this.hasCalculation) || (this.noInvoices && this.hasCalculation);
    },
    smAndUp() {
      return this.$vuetify.breakpoint.smAndUp;
    }
  },
  watch: {
    levyCollectionId() {
      this.reload();
    },
    providerId() {
      this.reload();
    },
    listingLoaded() {
      this.reload();
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    reload() {
      this.loaded = [];
      this.showing = [];
      this.pdfUrls = {};
      this.previewPdfUrl = null;
      this.showingPreview = false;
      const hasChanged =
        !this.listing.loaded ||
        this.listing.levyCollectionId != this.levyCollectionId ||
        this.listing.providerId != this.providerId;
      if (hasChanged) {
        this.$store.commit(mutation.LIST_SET_FILTERS, {
          levyCollectionId: this.levyCollectionId,
          providerId: this.providerId
        });
        this.populate();
      }
    },
    async loadPreviewPdf() {
      this.showingPreview = !this.showingPreview;
      if (this.showingPreview) {
        this.previewPdfUrl = `/api/levy-collections/${this.levyCollectionId}/providers/${this.providerId}/pdf-preview`;
      }
    },
    async showPdf(invoiceId) {
      const hide = this.loaded.includes(invoiceId);
      if (hide) {
        this.loaded.splice(this.loaded.indexOf(invoiceId), 1);
        this.showing.splice(this.showing.indexOf(invoiceId), 1);
        this.pdfUrls[invoiceId] = null;
      } else {
        this.showing.push(invoiceId);
        const pdfUrl = `/api/invoices/${invoiceId}/pdf`;
        this.pdfUrls[invoiceId] = pdfUrl;
        this.loaded.push(invoiceId);
      }
    },
    getStatusClass(status) {
      return this.isValidStatus(status) ? (status == "Cleared" ? "cleared-status" : "") : "invalid-status";
    },
    isValidStatus(status) {
      return this.doesLookupHaveAttribute(status, "InvoiceIsValid", this.lookups.invoiceStatuses);
    },
    open(invoiceId) {
      this.$router.push(`/levy-collections/${this.levyCollectionId}/invoices/${invoiceId}`);
    }
  }
};
</script>

<style lang="scss">
span.invalid-status {
  color: red;
}
span.cleared-status {
  color: green;
}
</style>